import { useState } from "react";
import { Platform, Pressable, Modal } from "react-native";
import styled from "styled-components/native";
import { colors, radius, font } from "@/lib/theme";
import { ModalContent } from "@/components/ContentWidth";

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  past?: boolean;
}

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
}

const PickerButton = styled.View`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  padding: 12px 14px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const PickerText = styled.Text<{ hasValue: boolean }>`
  font-size: 15px;
  color: ${(p) => p.hasValue ? colors.ink : colors.inkMuted};
  font-weight: ${font.medium};
`;

const PickerIcon = styled.Text`
  font-size: 16px;
  color: ${colors.inkMuted};
`;

const Sheet = styled.View`
  background-color: ${colors.white};
  border-top-left-radius: ${radius.xl}px;
  border-top-right-radius: ${radius.xl}px;
  padding: 16px;
  padding-bottom: 40px;
  align-items: center;
`;

const SheetTitle = styled.Text`
  font-size: 16px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 12px;
`;

const DoneBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 14px;
  align-items: center;
  width: 100%;
  margin-top: 12px;
`;

const DoneBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "Select date";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function formatTimeDisplay(timeStr: string): string {
  if (!timeStr) return "Select time";
  try {
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m} PM`;
    return `${hour === 0 ? 12 : hour}:${m} AM`;
  } catch {
    return timeStr;
  }
}

// ── Date Input ──

export function DateInput({ value, onChange, past }: DateInputProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(value ? new Date(value + "T00:00:00") : new Date());

  if (Platform.OS === "web") {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          backgroundColor: colors.bg,
          border: `1px solid ${colors.line}`,
          borderRadius: radius.md,
          paddingLeft: 14,
          paddingRight: 14,
          paddingTop: 12,
          paddingBottom: 12,
          fontSize: 15,
          color: colors.ink,
          fontFamily: "inherit",
          width: "100%",
          boxSizing: "border-box" as any,
          outline: "none",
        }}
      />
    );
  }

  const DateTimePicker = require("@react-native-community/datetimepicker").default;

  const handleDone = () => {
    const year = tempDate.getFullYear();
    const month = String(tempDate.getMonth() + 1).padStart(2, "0");
    const day = String(tempDate.getDate()).padStart(2, "0");
    onChange(`${year}-${month}-${day}`);
    setShowPicker(false);
  };

  return (
    <>
      <Pressable onPress={() => {
        setTempDate(value ? new Date(value + "T00:00:00") : new Date());
        setShowPicker(true);
      }}>
        <PickerButton>
          <PickerText hasValue={!!value}>{formatDateDisplay(value)}</PickerText>
          <PickerIcon>📅</PickerIcon>
        </PickerButton>
      </Pressable>
      <Modal visible={showPicker} animationType="slide" transparent>
        <ModalContent onClose={() => setShowPicker(false)}>
          <Sheet>
            <SheetTitle>Select Date</SheetTitle>
            <DateTimePicker
              value={tempDate}
              mode="date"
              display="inline"
              onChange={(_event: any, selectedDate?: Date) => {
                if (selectedDate) setTempDate(selectedDate);
              }}
              {...(past ? { maximumDate: new Date() } : { minimumDate: new Date() })}
              themeVariant="light"
              style={{ width: "100%", height: 340 }}
            />
            <Pressable onPress={handleDone} style={{ width: "100%" }}>
              <DoneBtn>
                <DoneBtnText>Done</DoneBtnText>
              </DoneBtn>
            </Pressable>
          </Sheet>
        </ModalContent>
      </Modal>
    </>
  );
}

// ── Time Input ──

export function TimeInput({ value, onChange }: TimeInputProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [tempTime, setTempTime] = useState<Date>(() => {
    const d = new Date();
    if (value) {
      const [h, m] = value.split(":");
      d.setHours(parseInt(h) || 0, parseInt(m) || 0);
    }
    return d;
  });

  if (Platform.OS === "web") {
    return (
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          backgroundColor: colors.bg,
          border: `1px solid ${colors.line}`,
          borderRadius: radius.md,
          paddingLeft: 14,
          paddingRight: 14,
          paddingTop: 12,
          paddingBottom: 12,
          fontSize: 15,
          color: colors.ink,
          fontFamily: "inherit",
          width: "100%",
          boxSizing: "border-box" as any,
          outline: "none",
        }}
      />
    );
  }

  const DateTimePicker = require("@react-native-community/datetimepicker").default;

  const handleDone = () => {
    const hours = String(tempTime.getHours()).padStart(2, "0");
    const mins = String(tempTime.getMinutes()).padStart(2, "0");
    onChange(`${hours}:${mins}`);
    setShowPicker(false);
  };

  return (
    <>
      <Pressable onPress={() => {
        const d = new Date();
        if (value) {
          const [h, m] = value.split(":");
          d.setHours(parseInt(h) || 0, parseInt(m) || 0);
        }
        setTempTime(d);
        setShowPicker(true);
      }}>
        <PickerButton>
          <PickerText hasValue={!!value}>{formatTimeDisplay(value)}</PickerText>
          <PickerIcon>🕐</PickerIcon>
        </PickerButton>
      </Pressable>
      <Modal visible={showPicker} animationType="slide" transparent>
        <ModalContent onClose={() => setShowPicker(false)}>
          <Sheet>
            <SheetTitle>Select Time</SheetTitle>
            <DateTimePicker
              value={tempTime}
              mode="time"
              display="spinner"
              is24Hour={false}
              minuteInterval={5}
              onChange={(_event: any, selectedTime?: Date) => {
                if (selectedTime) setTempTime(selectedTime);
              }}
              themeVariant="light"
              style={{ width: "100%", height: 200 }}
            />
            <Pressable onPress={handleDone} style={{ width: "100%" }}>
              <DoneBtn>
                <DoneBtnText>Done</DoneBtnText>
              </DoneBtn>
            </Pressable>
          </Sheet>
        </ModalContent>
      </Modal>
    </>
  );
}
